import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";
import newAPIService from "service/network/newAPIService";
import { getProfileLabel } from "../../navigo-module/components/UserTypeFilter";
import {
  queryMockCases,
  statMockCases,
  mockFilterOptions,
  mockAssigneeOptions,
  mockTriggerOptions,
  mockAccountCodeOptions,
} from "./mockData";

// ---------------------------------------------------------------------------
// Mock mode
// ---------------------------------------------------------------------------
// Serves the dashboard from a local fixture instead of api-ltr-service. Off by
// default now that the service is live; kept so the screen can still be
// reviewed against known data. While on, the dashboard never calls the API and
// renders a banner saying so — the banner is there precisely so mock mode
// cannot ship unnoticed.
//
// Per-tab override, no rebuild needed:
//   /miscellaneous/ltr/dashboard?mock=0   → use the real API
//   /miscellaneous/ltr/dashboard?mock=1   → back to sample data
//
// The override is remembered in sessionStorage for the tab. REACT_APP_LTR_MOCK
// is honoured too, so a dev server can set it without touching source.
const MOCK_BY_DEFAULT = false;

const MOCK_STORAGE_KEY = "ltr.mockMode";
const MOCK_LATENCY_MS = 250;

export function isMockMode() {
  try {
    const params = new URLSearchParams(window.location.search);
    const requested = params.get("mock");
    if (requested === "1" || requested === "true") {
      window.sessionStorage.setItem(MOCK_STORAGE_KEY, "1");
      return true;
    }
    if (requested === "0" || requested === "false") {
      window.sessionStorage.setItem(MOCK_STORAGE_KEY, "0");
      return false;
    }
    const stored = window.sessionStorage.getItem(MOCK_STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch (e) {
    // Private browsing can make sessionStorage throw. Fall through to the
    // default rather than failing the screen.
  }
  if (process.env.REACT_APP_LTR_MOCK === "true") return true;
  if (process.env.REACT_APP_LTR_MOCK === "false") return false;
  return MOCK_BY_DEFAULT;
}

// Answers on a later tick with a small delay, so loading states and skeletons
// render the way they will against a real network.
function respond(onResponse, payload) {
  setTimeout(() => onResponse(payload), MOCK_LATENCY_MS);
}

function respondWith(payload) {
  return new Promise((resolve) => {
    setTimeout(() => resolve(payload), MOCK_LATENCY_MS);
  });
}

// Builds the CSV the export endpoint would have streamed, so the Export button
// is reviewable in mock mode too.
function mockExportCsv(body) {
  const STATUS_LABELS = { 1: "New lead", 2: "In progress", 3: "Closed" };
  const OUTCOME_LABELS = {
    1: "Closed successfully",
    2: "Closed unsuccessfully",
    3: "False positive",
    4: "User not interested",
  };
  const ACTION_LABELS = {
    1: "Vital follow-up",
    2: "Hospital visit",
    3: "Diagnostic test",
    4: "Prescribe medicine",
    5: "Super specialist",
    6: "Follow-up consult",
  };
  const header = [
    "LTR ID", "User type", "Patient ID", "Risk level triggered by",
    "Source data date", "Risk stratification date", "Follow up date",
    "Assigned to", "Calls", "Action", "Status", "Outcome", "Closure reason",
  ];
  const fmt = (v) => {
    if (!v) return "";
    const d = new Date(v);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const escape = (v) => {
    const s = v === undefined || v === null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const { response } = queryMockCases(body);
  const rows = response.map((c) =>
    [
      c.ltrId,
      getProfileLabel(c.patientProfileType),
      c.patientId,
      (c.riskLevelTriggeredBy || []).join(", "),
      fmt(c.sourceDataDate),
      fmt(c.riskStratificationDate),
      fmt(c.followUpDate),
      c.assignedToName || "",
      c.callCount,
      ACTION_LABELS[c.action] || "",
      STATUS_LABELS[c.status] || "",
      c.status === 3 ? OUTCOME_LABELS[c.closureOutcome] || "" : "",
      c.closureReason || "",
    ].map(escape).join(","),
  );

  const csv = [header.map(escape).join(","), ...rows].join("\n");
  return {
    blob: new Blob([csv], { type: "text/csv" }),
    filename: "ltr-dashboard-export-mock.csv",
  };
}

class LTRService {
  constructor() {
    this.apiService = new APIService();
  }

  fetchDashboard = (body, onResponse, onError) => {
    if (isMockMode()) return respond(onResponse, queryMockCases(body));
    let url = APIConfig.ltr.dashboard;
    return this.apiService.post(url, body, onResponse, onError);
  };

  fetchDashboardStats = (body, onResponse, onError) => {
    if (isMockMode()) return respond(onResponse, statMockCases(body));
    let url = APIConfig.ltr.dashboardStats;
    return this.apiService.post(url, body, onResponse, onError);
  };

  // Export bypasses APIService because it returns a CSV stream rather than
  // JSON — we need the raw blob and the filename off Content-Disposition.
  exportDashboard = (body) => {
    if (isMockMode()) return respondWith(mockExportCsv(body));

    const url = APIConfig.ltr.export;
    const headers = {
      "Content-Type": "application/json",
    };
    const auth = newAPIService.headers || {};
    if (auth.client) headers.client = auth.client;
    if (auth.phone_no) headers.phone_no = auth.phone_no;
    if (auth.auth_token) headers.auth_token = auth.auth_token;

    return fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body || {}),
    }).then(async (res) => {
      if (!res.ok) {
        let detail = "";
        try {
          detail = await res.text();
        } catch (_) {}
        throw new Error(detail || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "ltr-dashboard-export.csv";
      return { blob, filename };
    });
  };

  // The three async column filters share the q/offset/limit contract that
  // ColumnFilterPopover's loadOptions expects.
  _fetchOptions = (baseUrl, { q = "", offset = 0, limit = 50 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (offset) params.set("offset", String(offset));
    if (limit) params.set("limit", String(limit));
    const qs = params.toString();
    const url = baseUrl + (qs ? `?${qs}` : "");
    return new Promise((resolve, reject) => {
      this.apiService.get(url, resolve, reject);
    });
  };

  fetchAssignees = (opts) => {
    if (isMockMode())
      return respondWith(mockFilterOptions(mockAssigneeOptions(), opts));
    return this._fetchOptions(APIConfig.ltr.assignees, opts);
  };

  fetchTriggerReasons = (opts) => {
    if (isMockMode())
      return respondWith(mockFilterOptions(mockTriggerOptions(), opts));
    return this._fetchOptions(APIConfig.ltr.triggerReasons, opts);
  };

  fetchAccountCodes = (opts) => {
    if (isMockMode())
      return respondWith(mockFilterOptions(mockAccountCodeOptions(), opts));
    return this._fetchOptions(APIConfig.ltr.accountCodes, opts);
  };

  // -------------------------------------------------------------------------
  // Case detail
  // -------------------------------------------------------------------------
  // The case carries only LTR state. Its AI summary and clinical data live on
  // the risk record (riskRecordId) and are read from the risk service, so a
  // re-evaluation there shows here without a sync step.

  _caseUrl = (id, suffix = "") => `${APIConfig.ltr.detail}/${id}${suffix}`;

  fetchCase = (id, onResponse, onError) =>
    this.apiService.get(this._caseUrl(id), onResponse, onError);

  fetchCaseAudits = (id, onResponse, onError) =>
    this.apiService.get(this._caseUrl(id, "/audits"), onResponse, onError);

  // Mirrors the doctor's AI-summary verdict onto the case. The full
  // evaluation is saved on the risk record; this keeps the case's own
  // evaluation fields and activity log in step.
  saveEvaluation = (id, body, onResponse, onError) =>
    this.apiService.post(this._caseUrl(id, "/evaluation"), body, onResponse, onError);

  // Opens a call on the case the moment it is placed; the new call is the
  // last entry of the returned case's `calls`.
  logCall = (id, onResponse, onError) =>
    this.apiService.post(this._caseUrl(id, "/call"), {}, onResponse, onError);

  // body: { connected } | { ended, outcome?, durationSeconds? } | { remark }
  updateCall = (id, callId, body, onResponse, onError) =>
    this.apiService.post(this._caseUrl(id, `/call/${callId}`), body, onResponse, onError);

  // Records an appointment Bridge has just booked against the case.
  // body: { appointmentId, callId, status, consultationAt, mode, serviceName,
  //         providerName, reason }
  linkAppointment = (id, body, onResponse, onError) =>
    this.apiService.post(this._caseUrl(id, "/appointment"), body, onResponse, onError);

  // body: { appointmentId, prescriptionId (string), prescriptionUrl }
  linkPrescription = (id, body, onResponse, onError) =>
    this.apiService.post(this._caseUrl(id, "/prescription"), body, onResponse, onError);

  // body: { appointmentId, actions, note }
  saveActions = (id, body, onResponse, onError) =>
    this.apiService.post(this._caseUrl(id, "/actions"), body, onResponse, onError);

  // ---- calling the patient (SparkTG via patient-communication) -------------

  fetchAlternatePhoneNos = (patientId, onResponse, onError) =>
    this.apiService.get(APIConfig.alternatePhoneNo.get.format(patientId), onResponse, onError);

  fetchSparktgAgent = (employeeId, onResponse, onError) =>
    this.apiService.get(APIConfig.callSparktg.getAgent.format(employeeId), onResponse, onError);

  // body: { employeeId, patientId, phoneNo } — phoneNo is an integer
  callPatient = (body, onResponse, onError) =>
    this.apiService.postv2(APIConfig.callSparktg.call, body, onResponse, onError);

  // ---- linking the Rx written in Bridge's prescription editor --------------

  // The patient's latest published prescription (one object, or undefined on
  // 204). Its id is how a newly published Rx is told apart from older ones.
  fetchLatestPrescription = (patientId, onResponse, onError) =>
    this.apiService.get(
      APIConfig.prescription.latestPrescription.format(patientId),
      onResponse,
      onError,
    );

  // Documents mapped to an appointment ({documents: [...]}, or undefined on
  // 204). The prescription service maps the Rx PDF here when the Rx carries
  // the appointment id.
  fetchAppointmentDocuments = (appointmentId, onResponse, onError) =>
    this.apiService.get(
      APIConfig.myPlan.navigo.getAppointmentDocMapping.format(appointmentId),
      onResponse,
      onError,
    );

  saveFollowUp = (id, followUpDate, onResponse, onError) =>
    this.apiService.post(
      this._caseUrl(id, "/follow-up"),
      { followUpDate },
      onResponse,
      onError,
    );

  // body: { employeeId, role: "doctor" | "coordinator", task, followUpDate }
  assignCase = (id, body, onResponse, onError) =>
    this.apiService.post(this._caseUrl(id, "/assign"), body, onResponse, onError);

  // body: { outcome, reason } — the reason is the optional closing note.
  closeCase = (id, body, onResponse, onError) =>
    this.apiService.post(this._caseUrl(id, "/close"), body, onResponse, onError);

  // Re-runs intake's DC account + LTR plan assignment for a case where it did
  // not land; safe to repeat.
  retryProgramme = (id, onResponse, onError) =>
    this.apiService.postAgain(this._caseUrl(id, "/programme"), {}, onResponse, onError);

  // The LTR doctor pool, with each doctor's open workload.
  // The LTR team, from the service's doctors and coordinators collections:
  // [{ id, role, name, email, phoneNo, speciality | team, openCases }].
  fetchDoctors = (onResponse, onError) =>
    this.apiService.get(APIConfig.ltr.doctors, onResponse, onError);

  fetchCoordinators = (onResponse, onError) =>
    this.apiService.get(APIConfig.ltr.coordinators, onResponse, onError);

  fetchPatient = (patientId, onResponse, onError) =>
    this.apiService.get(APIConfig.patient.get.format(patientId), onResponse, onError);
}

export default LTRService;
